import type { BackgroundLayer, BackgroundLayerPosition } from "../core/types"
import { TAILWIND_COLORS, DIRECTION_MAP } from "../runtime/effects-resolver"

export interface BackgroundLayersProps {
  layers: BackgroundLayer[]
}

export function BackgroundLayers({ layers }: BackgroundLayersProps) {
  if (!layers || layers.length === 0) return null

  return (
    <div
      className="pointer-events-none absolute inset-0 overflow-hidden"
      aria-hidden="true"
    >
      {layers.map((layer, i) => (
        <BackgroundLayerItem key={i} layer={layer} />
      ))}
    </div>
  )
}

function BackgroundLayerItem({ layer }: { layer: BackgroundLayer }) {
  switch (layer.type) {
    case "blur-orb":
      return <BlurOrb layer={layer} />
    case "animated-orb":
      return <AnimatedOrb layer={layer} />
    case "dot-pattern":
      return <DotPattern layer={layer} />
    case "grid-lines":
      return <GridLines layer={layer} />
    case "gradient":
      return <Gradient layer={layer} />
    case "radial-gradient":
      return <RadialGradient layer={layer} />
    case "image":
      return <ImageLayer layer={layer} />
    case "noise":
      return <NoiseTexture layer={layer} />
    default:
      return null
  }
}

// ── blur-orb ──────────────────────────────────────────────

function BlurOrb({ layer }: { layer: BackgroundLayer }) {
  const size = layer.size ?? 200
  const blur = layer.blur ?? 60
  const opacity = layer.opacity ?? 0.5

  return (
    <div
      className="absolute rounded-full"
      style={{
        width: size,
        height: size,
        backgroundColor: resolveColorToken(layer.color ?? "currentColor"),
        opacity,
        filter: `blur(${blur}px)`,
        ...positionToStyle(layer.position),
      }}
    />
  )
}

// ── animated-orb ──────────────────────────────────────────
// Floating blur orb with CSS animation (like 21st.dev hero sections)

let orbKeyframesInjected = false
function ensureOrbKeyframes(): void {
  if (orbKeyframesInjected) return
  orbKeyframesInjected = true
  const style = document.createElement("style")
  style.setAttribute("data-cogo-bg", "orb-keyframes")
  style.textContent = `
    @keyframes cogo-orb-float {
      0%, 100% { transform: translate(0, 0) scale(1); }
      25% { transform: translate(30px, -20px) scale(1.05); }
      50% { transform: translate(-10px, 20px) scale(0.95); }
      75% { transform: translate(-30px, -10px) scale(1.02); }
    }
  `
  document.head.appendChild(style)
}

function AnimatedOrb({ layer }: { layer: BackgroundLayer }) {
  ensureOrbKeyframes()
  const size = layer.size ?? 300
  const blur = layer.blur ?? 120
  const opacity = layer.opacity ?? 0.3
  const duration = layer.animationDuration ?? 20

  return (
    <div
      className="absolute rounded-full"
      style={{
        width: size,
        height: size,
        backgroundColor: resolveColorToken(layer.color ?? "primary"),
        opacity,
        filter: `blur(${blur}px)`,
        animation: `cogo-orb-float ${duration}s ease-in-out infinite`,
        ...positionToStyle(layer.position),
      }}
    />
  )
}

// ── dot-pattern ───────────────────────────────────────────

function DotPattern({ layer }: { layer: BackgroundLayer }) {
  const spacing = layer.spacing ?? 24
  const dotSize = layer.size ?? 1
  const opacity = layer.opacity ?? 0.3

  const maskStyle = buildMaskStyle(layer.mask, layer.maskSize)

  return (
    <div
      className="absolute inset-0"
      style={{
        backgroundImage: `radial-gradient(circle, currentColor ${dotSize}px, transparent ${dotSize}px)`,
        backgroundSize: `${spacing}px ${spacing}px`,
        opacity,
        ...maskStyle,
      }}
    />
  )
}

// ── grid-lines ────────────────────────────────────────────

function GridLines({ layer }: { layer: BackgroundLayer }) {
  const spacing = layer.spacing ?? 40
  const opacity = layer.opacity ?? 0.15

  const maskStyle = buildMaskStyle(layer.mask, layer.maskSize)

  return (
    <div
      className="absolute inset-0"
      style={{
        backgroundImage: [
          `linear-gradient(to right, currentColor 1px, transparent 1px)`,
          `linear-gradient(to bottom, currentColor 1px, transparent 1px)`,
        ].join(", "),
        backgroundSize: `${spacing}px ${spacing}px`,
        opacity,
        ...maskStyle,
      }}
    />
  )
}

// ── gradient ──────────────────────────────────────────────

function Gradient({ layer }: { layer: BackgroundLayer }) {
  let bg = layer.value ?? ""

  // Build gradient from token fields when value is not provided
  if (!layer.value && layer.from && layer.to) {
    const dir = DIRECTION_MAP[layer.direction ?? "to-b"] ?? layer.direction ?? "to bottom"
    const fromColor = resolveGradientToken(layer.from)
    const toColor = resolveGradientToken(layer.to)
    const via = layer.via ? resolveGradientToken(layer.via) : null
    const type = layer.gradient ?? "linear"

    if (type === "radial") {
      const stops = via ? `${fromColor}, ${via}, ${toColor}` : `${fromColor}, ${toColor}`
      bg = `radial-gradient(${stops})`
    } else {
      const stops = via ? `${fromColor}, ${via}, ${toColor}` : `${fromColor}, ${toColor}`
      bg = `linear-gradient(${dir}, ${stops})`
    }
  }

  return (
    <div
      className="absolute inset-0"
      style={{ background: bg }}
    />
  )
}

// ── radial-gradient ──────────────────────────────────────
// Advanced radial gradient with position/size (like 21st.dev mask effects)

function RadialGradient({ layer }: { layer: BackgroundLayer }) {
  const fromColor = resolveGradientToken(layer.from ?? "primary")
  const toColor = resolveGradientToken(layer.to ?? "transparent")
  const via = layer.via ? resolveGradientToken(layer.via) : null
  const opacity = layer.opacity ?? 1

  // Position: "50% 0%", "center top", etc.
  const gradientPosition = layer.gradientPosition ?? "50% 50%"
  // Size: "125% 125%", "circle", "ellipse farthest-corner", etc.
  const gradientSize = layer.gradientSize ?? "ellipse"

  const stops = via
    ? `${fromColor}, ${via}, ${toColor}`
    : `${fromColor}, ${toColor}`

  const bg = `radial-gradient(${gradientSize} at ${gradientPosition}, ${stops})`

  const maskStyle = buildMaskStyle(layer.mask, layer.maskSize)

  return (
    <div
      className="absolute inset-0"
      style={{
        background: bg,
        opacity,
        ...maskStyle,
      }}
    />
  )
}

// ── image ─────────────────────────────────────────────────

function ImageLayer({ layer }: { layer: BackgroundLayer }) {
  if (!layer.src) return null

  return (
    <img
      className="absolute inset-0 h-full w-full"
      src={layer.src}
      alt=""
      style={{
        objectFit: (layer.objectFit as React.CSSProperties["objectFit"]) ?? "cover",
        opacity: layer.opacity ?? 1,
      }}
    />
  )
}

// ── noise texture ────────────────────────────────────────
// SVG-based noise overlay (subtle grain texture)

function NoiseTexture({ layer }: { layer: BackgroundLayer }) {
  const opacity = layer.opacity ?? 0.05

  return (
    <div
      className="absolute inset-0"
      style={{
        opacity,
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
        backgroundRepeat: "repeat",
        backgroundSize: "256px 256px",
      }}
    />
  )
}

// ── helpers ───────────────────────────────────────────────

function positionToStyle(
  pos?: BackgroundLayerPosition,
): React.CSSProperties {
  if (!pos) return {}
  const style: React.CSSProperties = {}
  if (pos.top != null) style.top = pos.top
  if (pos.right != null) style.right = pos.right
  if (pos.bottom != null) style.bottom = pos.bottom
  if (pos.left != null) style.left = pos.left
  return style
}

function buildMaskStyle(
  mask?: string,
  maskSize?: string,
): React.CSSProperties {
  if (!mask) return {}
  const style: React.CSSProperties = {
    WebkitMaskImage: mask,
    maskImage: mask,
  }
  if (maskSize) {
    ;(style as Record<string, unknown>).WebkitMaskSize = maskSize
    ;(style as Record<string, unknown>).maskSize = maskSize
  }
  return style
}

// ── color token helpers ──────────────────────────────────

/** CSS-variable semantic tokens (used in shadcn themes) */
const CSS_VAR_TOKENS = new Set([
  "background", "foreground", "card", "card-foreground",
  "popover", "popover-foreground", "primary", "primary-foreground",
  "secondary", "secondary-foreground", "muted", "muted-foreground",
  "accent", "accent-foreground", "destructive", "destructive-foreground",
  "border", "input", "ring", "sidebar", "sidebar-foreground",
  // Status tokens
  "success", "success-foreground", "warning", "warning-foreground", "info", "info-foreground",
  // Chart tokens (shadcn charts)
  "chart-1", "chart-2", "chart-3", "chart-4", "chart-5",
])

/**
 * Resolve a color token to a valid CSS value.
 * Handles:
 *  - "token/opacity" → color-mix(in srgb, resolved 20%, transparent)
 *  - CSS-var semantic tokens: "primary" → var(--primary)
 *  - Tailwind named colors: "purple-500" → #a855f7
 *  - Raw CSS values: "#ff0000", "red", "rgb(...)" → pass through
 *  - "transparent", "currentColor" → pass through
 */
function resolveColorToken(color: string): string {
  if (!color) return color

  // Handle token/opacity format: "primary/20", "purple-500/15"
  const slashIdx = color.lastIndexOf("/")
  if (slashIdx > 0) {
    const colorPart = color.slice(0, slashIdx)
    const opacityPart = color.slice(slashIdx + 1)
    const opacity = parseInt(opacityPart, 10)
    if (!isNaN(opacity)) {
      const resolved = resolveSingleColorToken(colorPart)
      return `color-mix(in srgb, ${resolved} ${opacity}%, transparent)`
    }
  }

  return resolveSingleColorToken(color)
}

/** Resolve a single color token (no opacity suffix) to CSS */
function resolveSingleColorToken(token: string): string {
  // Pass through raw CSS values
  if (token.startsWith("#") || token.startsWith("rgb") || token.startsWith("hsl") || token.startsWith("var(")) {
    return token
  }
  // Pass through special keywords
  if (token === "transparent" || token === "currentColor" || token === "inherit") {
    return token
  }
  // CSS-variable semantic tokens
  if (CSS_VAR_TOKENS.has(token)) {
    return `var(--${token})`
  }
  // Tailwind named colors
  if (TAILWIND_COLORS[token]) {
    return TAILWIND_COLORS[token]
  }
  // Fallback: return as-is (may be a valid CSS color name like "red", "blue")
  return token
}

/**
 * Resolve a gradient color token to CSS — same as resolveSingleColorToken.
 * Used by the Gradient component for from/to fields.
 */
function resolveGradientToken(token: string): string {
  return resolveSingleColorToken(token)
}
